import { parseISO } from 'date-fns';
import { FunctionComponent, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Event } from '../../../../services';
import { IconLabel } from '../../IconLabel';
import { ImageGallery } from '../../ImageGallery';
import { LineClamp } from '../../LineClamp';
import { EventDateBadge, EventLocation } from '../EventItemContent';
import { eventDateTime, eventRegistrationDeadline } from '../helpers';

import classes from './EventItemPreview.module.scss';

interface EventItemPreviewProps {
  event: Event;
}

export const EventItemPreview: FunctionComponent<EventItemPreviewProps> = memo(({ event }) => {
  const { t: ctxT } = useTranslation('', { keyPrefix: 'eventItem' });

  const { name, image, startAt, descriptionHTML } = event;

  const startDate = useMemo(() => parseISO(startAt), [startAt]);

  const teaser = useMemo(() => {
    if (!image?.url) {
      return null;
    }
    return (
      <div className={classes['event-item-preview__image']}>
        <ImageGallery
          items={[{ image: { url: image.url } }]}
          singleImageFullSize
          modalPreviewOnClick
        />
        <EventDateBadge date={startDate} className={classes['event-item-preview__badge']} />
      </div>
    );
  }, [image, startDate]);

  const title = useMemo(() => {
    if (image?.url) {
      return <div className={classes['event-item-preview__title']}>{name}</div>;
    }
    return (
      <div className={classes['event-item-preview__header']}>
        <EventDateBadge date={startDate} />
        <div className={classes['event-item-preview__title']}>{name}</div>
      </div>
    );
  }, [image, name, startDate]);

  const description = useMemo(() => {
    if (!descriptionHTML) {
      return null;
    }
    return (
      <LineClamp
        maxLines={3}
        htmlStringContent={descriptionHTML}
        contentClassName={classes['event-item-preview__description']}
        readMore={'...'}
        nonClickable
      />
    );
  }, [descriptionHTML]);

  const info = useMemo(
    () => (
      <div className={classes['event-item-preview__info']}>
        <div className={classes['event-item-preview__info-row']}>
          <IconLabel iconId={'clock'} iconSize={16} nonClickable singleColor />
          {eventDateTime(event)}
        </div>

        <EventLocation event={event} />

        <div className={classes['event-item-preview__info-row']}>
          <IconLabel iconId={'event'} iconSize={16} nonClickable singleColor />
          {ctxT('registration-closes')}
          <strong>{eventRegistrationDeadline(event)}</strong>
        </div>
      </div>
    ),
    [ctxT, event]
  );

  return (
    <div className={classes['event-item-preview']}>
      {teaser}
      <div className={classes['event-item-preview__body']}>
        {title}
        {description}
        {info}
      </div>
    </div>
  );
});
